import type { AnyRouter } from '@tanstack/react-router';
import { useEffect } from 'react';
import { isNative, platform } from './platform';

/**
 * Native shell setup (Capacitor only): light status bar, hide the launch splash once the
 * web app has painted, and route the Android hardware back button through the router.
 */
export async function initNative(router: AnyRouter) {
  fitViewport();
  if (!isNative()) {
    registerServiceWorker();
    return;
  }
  const [{ StatusBar, Style }, { SplashScreen }, { App }] = await Promise.all([
    import('@capacitor/status-bar'),
    import('@capacitor/splash-screen'),
    import('@capacitor/app'),
  ]);
  try {
    await StatusBar.setStyle({ style: Style.Light });
    if (platform() === 'android') {
      await StatusBar.setOverlaysWebView({ overlay: true });
    }
  } catch {
    /* not available */
  }
  void SplashScreen.hide();

  void App.addListener('backButton', ({ canGoBack }) => {
    // Close an open sheet first (sheets live in the `sheet` search param)
    const search = router.state.location.search as Record<string, unknown>;
    if (search.sheet) {
      void router.navigate({
        to: '.',
        search: (s: Record<string, unknown>) => ({ ...s, sheet: undefined }),
      } as never);
      return;
    }
    const path = router.state.location.pathname;
    if (canGoBack && !['/hub', '/sign-in', '/'].includes(path)) router.history.back();
    else void App.minimizeApp();
  });
}

/**
 * iOS zooms in on any focused field under 16px (ours are 15px) and stays zoomed, which leaves the whole
 * app draggable sideways. `maximum-scale=1` stops that; iOS Safari still lets people pinch-zoom. The
 * native apps don't zoom at all, like any other app. Android browsers are left alone: they don't
 * auto-zoom, and blocking pinch-zoom there would hurt accessibility.
 */
function fitViewport() {
  const ios =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1); // iPadOS reports as a Mac
  if (!ios && !isNative()) return;
  document
    .querySelector('meta[name="viewport"]')
    ?.setAttribute(
      'content',
      `width=device-width, initial-scale=1, maximum-scale=1${isNative() ? ', user-scalable=no' : ''}, viewport-fit=cover`,
    );
}

function registerServiceWorker() {
  if (import.meta.env.DEV || !('serviceWorker' in navigator)) return;
  void import('virtual:pwa-register').then(({ registerSW }) => registerSW({ immediate: true }));
}

/**
 * Light status-bar text while a dark full-screen view (the scanners) is showing; dark text again after.
 * Only when the page actually draws under the status bar: Android WebViews older than 140 can't, so
 * Capacitor pads the window instead and the bar sits on a white strip that needs the dark text.
 */
export function useDarkStatusBar(active = true) {
  useEffect(() => {
    if (!active || !isNative() || !drawsUnderStatusBar()) return;
    const set = (dark: boolean) =>
      import('@capacitor/status-bar')
        .then(({ StatusBar, Style }) => StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light }))
        .catch(() => {});
    void set(true);
    return () => void set(false);
  }, [active]);
}

function drawsUnderStatusBar(): boolean {
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;visibility:hidden;padding-top:env(safe-area-inset-top)';
  document.body.appendChild(probe);
  const inset = Number.parseFloat(getComputedStyle(probe).paddingTop) || 0;
  probe.remove();
  return inset > 0;
}
